import React, { useState } from 'react';
import { X } from 'lucide-react';
import useLmsContentFileUrl from '../../../hooks/useLmsContentFileUrl';
import CourseAudioPlayer from '../computer-apps/CourseAudioPlayer';
import CourseImageViewer from '../computer-apps/CourseImageViewer';

// Files come through .../courses/learning-zone/content/:id/file, which opens a
// published course's file and records the view in the student's progress.
const COURSE_SLUG = 'learning-zone';

const isExternalVideo = (url = '') => /youtube\.com|youtu\.be|vimeo\.com/i.test(url);

// Shown instead of the file when the server can't fetch it - e.g. it is still
// linked to the old Amazon S3 bucket, which no longer allows any access.
const UNAVAILABLE = "This file can't be opened right now. Please ask your teacher to upload it again.";

function Loading({ label }) {
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="rounded-2xl bg-white px-8 py-6 font-bold text-gray-700 shadow-2xl">{label}</div>
    </div>
  );
}

/**
 * Opens one content item of a course task type row over the Learning Zone: a
 * PDF or reading in a document window, a video in a player, audio and pictures
 * in the players the Computer Apps course uses. Games open in a new tab and
 * quizzes in the Learning Zone itself (LearningZoneQuiz) instead.
 */
export default function LearningContentViewer({ item, onClose }) {
  const studentId = localStorage.getItem('userId');
  // Videos stream from a signed link; other files load through the API.
  const { url, loading, error } = useLmsContentFileUrl(COURSE_SLUG, item, {
    studentId,
    preferSignedUrl: item.type === 'video',
  });
  // A signed video link is made without checking the file exists, so the player reports it.
  const [videoFailed, setVideoFailed] = useState(false);
  // On a failed request the hook falls back to the stored link, which the browser
  // can't open either (it showed the storage's raw error page), so it isn't used.
  const unavailable = Boolean(error) || videoFailed;

  if (item.type === 'audio' && !unavailable) {
    return loading ? <Loading label="Loading audio..." /> : <CourseAudioPlayer audioUrl={url} title={item.title} onClose={onClose} />;
  }
  if (item.type === 'image' && !unavailable) {
    return loading ? <Loading label="Loading picture..." /> : <CourseImageViewer imageUrl={url} title={item.title} onClose={onClose} />;
  }

  const isVideo = item.type === 'video' && !unavailable;
  const text = item.textContent || item.description;

  return (
    <div className="fixed inset-0 bg-gray-900/75 z-50 flex items-center justify-center p-4">
      {/* A video window narrows on short screens so its 16:9 player plus the
          ~4.5rem title bar stays within 90vh - otherwise the player's controls
          were cut off at the bottom. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={item.title}
        className={`w-full flex flex-col overflow-hidden rounded-2xl shadow-2xl ${
          isVideo
            ? 'max-w-[min(64rem,calc((90vh_-_4.5rem)*16/9))] max-h-[90vh] bg-black'
            : 'max-w-4xl h-[85vh] bg-white'
        }`}
      >
        <div
          className={`flex items-center justify-between gap-3 px-5 py-4 border-b ${
            isVideo ? 'bg-gray-900 border-gray-800 text-white' : 'bg-gray-50 border-gray-100 text-gray-900'
          }`}
        >
          <h3 className="text-lg font-extrabold truncate">{item.title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`p-2 rounded-full transition-colors ${isVideo ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-200'}`}
          >
            <X size={22} />
          </button>
        </div>

        {isVideo ? (
          <div className="relative aspect-video bg-black">
            {loading && <div className="absolute inset-0 flex items-center justify-center text-white">Loading video...</div>}
            {!loading && url && isExternalVideo(url) && (
              <iframe src={url.replace('watch?v=', 'embed/')} title={item.title} className="absolute inset-0 h-full w-full" allowFullScreen />
            )}
            {!loading && url && !isExternalVideo(url) && (
              <video
                src={url}
                title={item.title}
                controls
                autoPlay
                onError={() => setVideoFailed(true)}
                className="absolute inset-0 h-full w-full"
              />
            )}
            {!loading && !url && <div className="absolute inset-0 flex items-center justify-center text-white">Video unavailable</div>}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto bg-white">
            {loading && <div className="flex h-full items-center justify-center text-gray-600">Loading...</div>}
            {!loading && unavailable && (
              <p role="alert" className="flex h-full items-center justify-center p-8 text-center text-lg font-bold text-gray-600">
                {UNAVAILABLE}
              </p>
            )}
            {!loading && !unavailable && url && <iframe src={url} title={item.title} className="h-full w-full" />}
            {!loading && !unavailable && !url && (
              <p className="whitespace-pre-wrap p-8 text-lg leading-relaxed text-gray-800">{text || 'Nothing to show yet.'}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
